import type { TranscriptSpeakerParticipantMetadata } from "@/modules/audio-recording/transcript-types";
import { resolveTranscriptSpeakerIsPlatformHost } from "@/modules/audio-recording/transcript-speaker-platform-host";

export interface SpeakerParticipantSource {
  participantId: number | string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata;
}

function rosterKey(id: number | string | null | undefined): string | null {
  if (id == null) {
    return null;
  }

  const key = String(id).trim();
  return key === "" ? null : key;
}

function mergeParticipants(
  primary: TranscriptSpeakerParticipantMetadata | null | undefined,
  secondary: TranscriptSpeakerParticipantMetadata | null | undefined
): TranscriptSpeakerParticipantMetadata {
  const participant: TranscriptSpeakerParticipantMetadata = {};
  const id = primary?.id ?? secondary?.id;
  const name = primary?.name ?? secondary?.name;
  const isPlatformHost =
    resolveTranscriptSpeakerIsPlatformHost({ source: primary }) ??
    resolveTranscriptSpeakerIsPlatformHost({ source: secondary });
  const platform = primary?.platform ?? secondary?.platform;
  const email = primary?.email ?? secondary?.email;
  const extraData = primary?.extraData ?? secondary?.extraData;

  if (id != null) participant.id = id;
  if (name != null) participant.name = name;
  if (isPlatformHost != null) participant.isPlatformHost = isPlatformHost;
  if (platform != null) participant.platform = platform;
  if (email != null) participant.email = email;
  if (extraData != null) participant.extraData = extraData;

  return participant;
}

function hasParticipantMetadata(participant: TranscriptSpeakerParticipantMetadata): boolean {
  return (
    participant.id != null ||
    participant.name != null ||
    resolveTranscriptSpeakerIsPlatformHost({ source: participant }) != null ||
    participant.platform != null ||
    participant.email != null ||
    participant.extraData != null
  );
}

export class SpeakerParticipantRoster {
  private readonly participantsById = new Map<string, TranscriptSpeakerParticipantMetadata>();

  reset(): void {
    this.participantsById.clear();
  }

  remember(source: SpeakerParticipantSource): void {
    const participant = source.speakerParticipant;
    if (!participant) {
      return;
    }

    const key = rosterKey(participant.id ?? source.participantId);
    if (key === null) {
      return;
    }

    this.participantsById.set(key, mergeParticipants(participant, this.participantsById.get(key)));
  }

  resolve(source: SpeakerParticipantSource): TranscriptSpeakerParticipantMetadata | undefined {
    const inline = source.speakerParticipant;
    const key = rosterKey(inline?.id ?? source.participantId);
    const fromRoster = key !== null ? this.participantsById.get(key) : undefined;

    const merged = mergeParticipants(inline, fromRoster);
    if (!hasParticipantMetadata(merged)) {
      return undefined;
    }

    if (key !== null) {
      this.participantsById.set(key, merged);
    }

    return merged;
  }
}
